import type { TimeInterval } from 'services/ClaimscoreApiService/shared/types';
import { formatDateRangeReadable } from './dates';
import { getQuarterLabel, isQuarterFormat } from './getQuarterDate';
import { getMonthFullName } from './monthAbbreviations';
import { FULL_MONTHS, SHORT_MONTHS } from './months';

const HOURS_PER_DAY = 24;
const NOON = 12;
const LAST_HOUR_AM = 11;
const FOUR_HOUR_BLOCK = 4;
const HOUR_RANGE_REGEX =
  /(?<hour>\d{1,2}):(?<minutes>\d{2})\s(?<period>AM|PM)/iu;

const getMonthName = (
  monthNum: number,
  isShort: boolean = false,
): string | undefined => {
  const months = isShort ? SHORT_MONTHS : FULL_MONTHS;
  return months[monthNum - 1];
};

const formatTime = (hours: number, minutes: number): string => {
  const period = hours >= NOON ? 'PM' : 'AM';
  const displayHours = hours % NOON || NOON;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${period}`;
};

const formatRange = (
  start: string,
  end: string,
  formatter: (date: string) => string | null,
): string => {
  const formattedStart = formatter(start);
  if (formattedStart && start !== end) {
    const formattedEnd = formatter(end);
    if (formattedEnd) {
      return `${formattedStart} – ${formattedEnd}`;
    }
  }
  return formattedStart || start;
};

export const formatSpikeDateRange = (
  startDate: string,
  endDate: string,
  timeInterval?: TimeInterval | string,
): string => {
  if (!timeInterval) {
    return formatSpikeDateRangeByFormat(startDate, endDate);
  }

  switch (timeInterval) {
    case 'quarter':
      return formatQuarterRange(startDate, endDate);
    case 'month':
      return formatMonthRange(startDate);
    case 'week':
      return formatWeekRange(startDate, endDate);
    case 'day':
      return formatDayRange(startDate);
    case 'fourHourBlock':
      return formatFourHourBlockRange(startDate);
    case 'hour':
      return formatHourRange(startDate);
    case 'year':
      return startDate;
    default:
      return formatSpikeDateRangeByFormat(startDate, endDate);
  }
};

const formatSpikeDateRangeByFormat = (
  startDate: string,
  endDate: string,
): string => {
  if (isQuarterFormat(startDate)) {
    const formattedStart = getQuarterLabel(startDate);

    if (formattedStart && startDate !== endDate && isQuarterFormat(endDate)) {
      const formattedEnd = getQuarterLabel(endDate);
      if (formattedEnd) {
        return `${formattedStart} – ${formattedEnd}`;
      }
    }

    return formattedStart || startDate;
  }

  if (startDate.includes("'")) {
    const [monthAbbr, year] = startDate.split("'");
    const fullMonth = getMonthFullName(monthAbbr);

    if (startDate !== endDate && endDate.includes("'")) {
      const [endMonthAbbr, endYear] = endDate.split("'");
      const endFullMonth = getMonthFullName(endMonthAbbr);
      if (fullMonth && endFullMonth) {
        return `${fullMonth}, ${year} – ${endFullMonth}, ${endYear}`;
      }
    }

    return fullMonth ? `${fullMonth}, ${year}` : startDate;
  }

  return formatDateRangeReadable(startDate, endDate);
};

const formatQuarterRange = (startDate: string, endDate: string): string => {
  return formatRange(startDate, endDate, (date) => getQuarterLabel(date));
};

const formatMonthRange = (startDate: string): string => {
  if (!startDate.includes("'")) return startDate;
  const [monthAbbr, year] = startDate.split("'");
  const fullMonth = getMonthFullName(monthAbbr);
  const fullYear = year.length === 2 ? `20${year}` : year;
  return fullMonth ? `${fullMonth}, ${fullYear}` : startDate;
};

const formatWeekRange = (startDate: string, endDate: string): string => {
  const [startMonth, startDay] = startDate.split('/');
  const [endMonth, endDay] = endDate.split('/');

  const startMonthName = getMonthName(parseInt(startMonth, 10), true);
  const endMonthName = getMonthName(parseInt(endMonth, 10), true);
  const startDayNum = parseInt(startDay, 10);
  const endDayNum = parseInt(endDay, 10);

  if (startMonth === endMonth) {
    return `${startMonthName} ${startDayNum} – ${endDayNum}`;
  }

  return `${startMonthName} ${startDayNum} – ${endMonthName} ${endDayNum}`;
};

const formatDayRange = (startDate: string): string => {
  const [month, day] = startDate.split('/');
  const monthName = getMonthName(parseInt(month, 10), false);
  return `${monthName} ${parseInt(day, 10)}`;
};

const formatFourHourBlockRange = (startDate: string): string => {
  const [date, time] = startDate.split(' ');
  const [month, day] = date.split('/');
  const [hours, minutes] = time.split(':');

  const monthName = getMonthName(parseInt(month, 10), true);
  const dayNum = parseInt(day, 10);
  const hourNum = parseInt(hours, 10);
  const minNum = parseInt(minutes, 10);

  const startTime = formatTime(hourNum, minNum);
  const endHours = hourNum + 4;

  if (endHours < HOURS_PER_DAY) {
    const endTime = formatTime(endHours, minNum);
    return `${monthName} ${dayNum}, ${startTime} - ${monthName} ${dayNum}, ${endTime}`;
  }

  const endDay = dayNum + 1;
  const endHoursWrapped = endHours - HOURS_PER_DAY;
  const endTime = formatTime(endHoursWrapped, minNum);
  return `${monthName} ${dayNum}, ${startTime} - ${monthName} ${endDay}, ${endTime}`;
};

const formatHourRange = (startDate: string): string => {
  const match = startDate.match(HOUR_RANGE_REGEX);
  if (!match?.groups) return startDate;

  const { hour, minutes, period } = match.groups;
  const hourNum = parseInt(hour, 10);
  const upperPeriod = period.toUpperCase();
  const startTime = `${hourNum}:${minutes} ${upperPeriod}`;
  let endHour: number;
  let endPeriod: string;

  if (hourNum === NOON) {
    endHour = 1;
    endPeriod = upperPeriod;
  } else if (hourNum === LAST_HOUR_AM && upperPeriod === 'AM') {
    endHour = NOON;
    endPeriod = 'PM';
  } else if (hourNum === LAST_HOUR_AM && upperPeriod === 'PM') {
    endHour = NOON;
    endPeriod = 'AM';
  } else {
    endHour = hourNum + 1;
    endPeriod = upperPeriod;
  }

  const endTime = `${endHour}:${minutes} ${endPeriod}`;
  return `${startTime} - ${endTime}`;
};

export const extractFourHourBlockDateRange = (startDate: string): string => {
  const [startDatePart] = startDate.split(' ');
  const [month, day] = startDatePart.split('/');
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const monthName = getMonthName(monthNum, true);
  return `${monthName} ${dayNum}`;
};

export const extractFourHourBlockTimeRange = (startDate: string): string => {
  const [, time] = startDate.split(' ');
  const [hours, minutes] = time.split(':');

  const hourNum = parseInt(hours, 10);
  const startTime = formatTime(hourNum, parseInt(minutes, 10));
  const endHours = hourNum + FOUR_HOUR_BLOCK;

  if (endHours < HOURS_PER_DAY) {
    const endTime = formatTime(endHours, parseInt(minutes, 10));
    return `${startTime} - ${endTime}`;
  }

  const wrappedHours = endHours - HOURS_PER_DAY;
  const endTime = formatTime(wrappedHours, parseInt(minutes, 10));
  return `${startTime} - ${endTime}`;
};
